import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Film, Image, Loader2, Trash2, Type, Upload } from "lucide-react";
import { HeroCaptionDialog } from "@/components/admin/HeroCaptionDialog";

const ACCEPT = "image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime,video/mpeg";
const INTERVALS = [3, 4, 5, 6, 8, 10, 15];

const SlideCard = ({ slide, i, total, onMove, onDelete, onCaption }) => (
  <div className="relative rounded-xl overflow-hidden border border-border bg-muted/20 group" data-testid={`hero-slide-card-${slide.id}`}>
    {slide.type === "video"
      ? <video src={slide.url} className="w-full aspect-video object-cover" muted playsInline preload="metadata" />
      : <img src={slide.url} alt="" className="w-full aspect-video object-cover" />}
    <span className="absolute top-2 left-2 h-6 min-w-6 px-2 rounded-full bg-black/70 text-white text-xs font-semibold flex items-center gap-1">
      {slide.type === "video" ? <Film className="w-3 h-3" /> : <Image className="w-3 h-3" />}{i + 1}
    </span>
    {slide.headline && <p className="absolute top-2 left-12 right-2 text-xs font-semibold text-white truncate drop-shadow" data-testid={`hero-slide-headline-${slide.id}`}>{slide.headline}</p>}
    <div className="absolute inset-x-0 bottom-0 p-2 flex justify-between bg-gradient-to-t from-black/70 to-transparent">
      <div className="flex gap-1">
        <Button size="icon" variant="secondary" className="h-7 w-7" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`hero-slide-left-${slide.id}`}><ArrowLeft className="w-3.5 h-3.5" /></Button>
        <Button size="icon" variant="secondary" className="h-7 w-7" disabled={i === total - 1} onClick={() => onMove(i, 1)} data-testid={`hero-slide-right-${slide.id}`}><ArrowRight className="w-3.5 h-3.5" /></Button>
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="secondary" className="h-7 px-2 text-xs gap-1" onClick={() => onCaption(slide)} data-testid={`hero-slide-caption-${slide.id}`}><Type className="w-3.5 h-3.5" />{slide.headline || slide.button_label ? "Edit caption" : "Add caption"}</Button>
        <Button size="icon" variant="destructive" className="h-7 w-7" onClick={() => onDelete(slide)} data-testid={`hero-slide-delete-${slide.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  </div>
);

export const HeroSlidesManager = ({ headers }) => {
  const [slides, setSlides] = useState([]);
  const [interval, setIntervalSec] = useState(5);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => axios.get(`${API}/settings/homepage`).then((r) => {
    setSlides(r.data.hero_slides || []);
    setIntervalSec(r.data.hero_interval || 5);
  });
  useEffect(() => { load().catch(() => {}); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setUploading(true);
    for (const f of files) {
      const max = f.type.startsWith("video/") ? 50 : 10;
      if (f.size > max * 1024 * 1024) { toast.error(`"${f.name}" is over ${max}MB — skipped`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        await axios.post(`${API}/admin/hero-slides`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      } catch (err) { toast.error(`"${f.name}": ${err.response?.data?.detail || "upload failed"}`); }
    }
    await load();
    setUploading(false);
    toast.success("Hero slides updated");
  };

  const move = async (i, step) => {
    const next = [...slides];
    [next[i], next[i + step]] = [next[i + step], next[i]];
    setSlides(next);
    await axios.put(`${API}/admin/hero-slides/reorder`, { order: next.map((s) => s.id) }, { headers });
  };

  const remove = async (slide) => {
    if (!window.confirm("Remove this slide from the homepage hero?")) return;
    await axios.delete(`${API}/admin/hero-slides/${slide.id}`, { headers });
    setSlides((s) => s.filter((x) => x.id !== slide.id));
    toast.success("Slide removed");
  };

  const saveInterval = async (v) => {
    setIntervalSec(v);
    await axios.put(`${API}/admin/hero-settings`, { interval: v }, { headers });
    toast.success(`Slides now change every ${v}s`);
  };

  return (
    <div className="space-y-3" data-testid="hero-slides-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="text-sm font-medium text-foreground flex items-center gap-2"><Image className="w-4 h-4" /> Hero Carousel ({slides.length}/10)</label>
        <label className="text-xs text-muted-foreground flex items-center gap-2">
          Change photo every
          <select value={interval} onChange={(e) => saveInterval(Number(e.target.value))} className="border border-border rounded-md px-2 py-1 bg-background text-foreground" data-testid="hero-interval-select">
            {INTERVALS.map((v) => <option key={v} value={v}>{v}s</option>)}
          </select>
        </label>
      </div>
      {slides.length === 0
        ? <p className="text-sm text-muted-foreground" data-testid="hero-slides-empty">No slides yet — the default hero image is shown.</p>
        : <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {slides.map((s, i) => <SlideCard key={s.id} slide={s} i={i} total={slides.length} onMove={move} onDelete={remove} onCaption={setEditing} />)}
          </div>}
      <HeroCaptionDialog slide={editing} headers={headers} onClose={() => setEditing(null)} onSaved={(u) => setSlides((all) => all.map((x) => (x.id === u.id ? u : x)))} />
      <div className="border-2 border-dashed border-border rounded-xl p-4 text-center">
        <input type="file" multiple accept={ACCEPT} onChange={upload} className="hidden" id="hero-slide-upload" data-testid="hero-slide-input" disabled={uploading || slides.length >= 10} />
        <label htmlFor="hero-slide-upload" className="cursor-pointer inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Uploading..." : "Add slides (Images JPG/PNG/WebP ≤10MB · Videos MP4/WebM/MOV ≤50MB). Videos play to the end, then advance."}
        </label>
      </div>
    </div>
  );
};
